// Module-level store for the initial release identifier captured at
// session start. Shared between `useVersionPoll` (which captures the
// value on first successful fetch) and the app-router error
// boundaries (which read it on demand when a chunk load fails, to
// decide whether an observed error is a stale-bundle artefact of a
// new deploy or something else entirely).
//
// A plain module-scoped `let` — deliberately not a Jotai atom or a
// React context. The value is read imperatively at a single moment
// (error boundary render time), never reactively; any state library
// would be incidental ceremony.
//
// The store is imported only from `'use client'` modules (the hook
// and the error boundaries) and therefore never runs on the Node
// server. No SSR cross-request persistence concern.

let initialVersion: string | null = null;

// Write-once per module lifecycle: the first non-empty value wins.
// This is intentional — we want the *session's* initial version as a
// stable baseline, not the most-recently-observed one. Silently
// ignoring later writes keeps the baseline pinned even if a test or
// a stray caller tries to overwrite it.
export const setInitialVersion = (value: string): void => {
  if (initialVersion === null && value !== '') {
    initialVersion = value;
  }
};

export const getInitialVersion = (): string | null => initialVersion;

// Test-only: reset between Jest tests. The double-underscore prefix
// advertises that this is not part of the module's production API.
export const __resetInitialVersion = (): void => {
  initialVersion = null;
};
